import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Drawer, Form, Input, Popconfirm, Popover, Space, Tag, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, FileProtectOutlined, PlusOutlined } from '@ant-design/icons';
import Axios from 'axios';
import { toast } from 'react-toastify';

const { Text } = Typography;
const bp = '/bp';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });
const CCR_STATUS_COLOR = { DRAFT: 'default', RAISED: 'processing', APPROVED: 'success', REJECTED: 'error', CLOSED: 'default' };

/**
 * Change Control Request list + raise/close for one target record — reused wherever a
 * versioned master record (MFR, Recipe/MBMR, ...) needs "new version" gated behind an
 * approved, still-open CCR (see BMR_INGESTION_PLAN.md "Change Control Request (CCR) gate").
 * Approve/reject/return reuse ApprovalPanel as-is (objectType="ChangeControl").
 *
 * targetObjectType: the same ApprovalTransaction.OBJECT_* value the target itself uses when
 * submitted for its OWN approval — "MFR", "Recipe", etc.
 * 2026-09-02, "need unified part" — approval no longer happens in this panel at all: raising a
 * CCR now opens a real Process Request (the "PR" page, ticketworkflowapp) and approval happens
 * there, on the same ticket queue every other request goes through; ccr.status is kept in sync
 * FROM that ticket server-side (change_control.sync_from_ticket) so this panel only ever reads
 * it, same as before — it just links to the real ticket instead of embedding ApprovalPanel.
 * onOpenCcrChange(ccr | null): called whenever the currently-licensing (APPROVED, not closed)
 * CCR changes, so the caller can gate its own "new version" action on it.
 * compact: 2026-09-01, "move change control to right top with button icon" — render just a
 * small icon button (badged with the open-CCR count) that reveals the exact same content in a
 * Popover on click, instead of the always-expanded block. Same component/state either way —
 * only the outer wrapper differs — so this stays a drop-in swap wherever it's already used.
 * disabled / disabledReason: 2026-09-01, "disable it as long as we can edit the recipe or any
 * for that matter, default setting this one" — a CCR only ever matters for the "new version"
 * action on an already-immutable (APPROVED/SUPERSEDED) record; while the record is still
 * editable there's nothing for a change control to gate, so the caller passes disabled=true for
 * that case instead of this component guessing at status values it doesn't own.
 */
export default function ChangeControlPanel({ targetObjectType, targetId, onOpenCcrChange, compact, disabled, disabledReason }) {
  const navigate = useNavigate();
  const [ccrs, setCcrs] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [raising, setRaising] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (!targetId) { setCcrs([]); return; }
    try {
      const res = await Axios.get(`${bp}/change-control/list/`, { params: { target_object_type: targetObjectType, target_id: targetId }, ...auth() });
      setCcrs(res?.data?.data || []);
    } catch (e) { /* leave as-is */ }
  }, [targetObjectType, targetId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (onOpenCcrChange) onOpenCcrChange(ccrs.find((c) => c.status === 'APPROVED') || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccrs]);

  const raise = async () => {
    let v; try { v = await form.validateFields(); } catch (e) { return; }
    setRaising(true);
    try {
      await Axios.post(`${bp}/change-control/raise/`, { ...v, target_object_type: targetObjectType, target_id: targetId }, auth());
      toast.success('Change control request raised');
      form.resetFields(); setDrawerOpen(false);
      await load();
    } catch (e) { toast.warning(e?.response?.data?.message || 'Could not raise the change control request'); }
    setRaising(false);
  };

  const close = async (id) => {
    try {
      await Axios.post(`${bp}/change-control/close/`, { id }, auth());
      toast.success('Change control request closed');
      await load();
    } catch (e) { toast.warning(e?.response?.data?.message || 'Could not close the change control request'); }
  };

  if (!targetId) return null;

  const openCount = ccrs.filter((c) => c.status !== 'CLOSED' && c.status !== 'REJECTED').length;

  const listContent = (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Button size="small" icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)} style={{ alignSelf: 'flex-start' }}>Raise change control request</Button>
      {ccrs.length === 0 && <Text type="secondary">No change control requests yet — a new version can't be created until one is raised and approved.</Text>}
      {ccrs.map((c) => (
        <Card key={c.id} size="small" type="inner" title={<Space><Text strong>{c.ccr_no}</Text><Tag color={CCR_STATUS_COLOR[c.status] || 'default'}>{c.status}</Tag>{c.title}</Space>}
          extra={c.status === 'APPROVED' && <Popconfirm title="Close this change control request?" description="A further edit will need a fresh CCR." onConfirm={() => close(c.id)}>
            <Button size="small" danger>Close</Button>
          </Popconfirm>}>
          {c.description && <Text type="secondary">{c.description}</Text>}
          {/* 2026-09-02, "need unified part" — a raised CCR now IS a real Process Request; approve/
              reject happens on the "PR" page (the same ticket queue every other request uses),
              not here. Link straight to it instead of embedding the (now-retired) ApprovalPanel. */}
          {c.status !== 'CLOSED' && c.ticket_id && (
            <Button size="small" type="link" style={{ padding: 0 }} icon={<ArrowRightOutlined />}
              onClick={() => navigate(`/ticket/${c.ticket_id}`)}>
              {c.status === 'APPROVED' ? 'View in Process Requests' : `Approve in Process Requests${c.ticket_unique_id ? ` (${c.ticket_unique_id})` : ''}`}
            </Button>
          )}
          {c.status !== 'CLOSED' && !c.ticket_id && (
            <Text type="secondary">No Process Request linked — raised before this existed.</Text>
          )}
          {c.status === 'CLOSED' && <Text type="secondary">Closed {String(c.closed_at).slice(0, 16)} by {c.closed_by || '—'}{c.closure_notes ? ` — ${c.closure_notes}` : ''}</Text>}
        </Card>
      ))}
    </Space>
  );

  const raiseDrawer = (
    <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} width={480} title="Raise change control request"
      extra={<Space><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button type="primary" loading={raising} onClick={raise}>Raise</Button></Space>}>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'What is this change for?' }]}>
          <Input placeholder="e.g. Update excipient overage per stability data" />
        </Form.Item>
        <Form.Item name="description" label="Description"><Input.TextArea rows={4} placeholder="Optional detail for the approver" /></Form.Item>
      </Form>
    </Drawer>
  );

  if (compact) {
    const button = (
      <Badge count={openCount} size="small" offset={[-2, 2]}>
        <Button size="small" icon={<FileProtectOutlined />} disabled={disabled}>Change control</Button>
      </Badge>
    );
    if (disabled) {
      return <Tooltip title={disabledReason || 'Not needed yet — only required to create a new version once this record is approved'}>{button}</Tooltip>;
    }
    return (
      <>
        {/* 2026-09-02, "now CCR is approved but cannot edit this?" — real staleness gap: approval
            now happens on a separate page (the ticket/PR page, "need unified part"), so this
            panel has no way to know a CCR was just approved elsewhere unless it re-fetches.
            Backend confirmed correct (require_open_ccr succeeds right now for the real CCR) —
            re-check on every open, the same natural moment a user would look here to act on it. */}
        <Popover trigger="click" placement="bottomRight" title="Change control" content={<div style={{ width: 340 }}>{listContent}</div>}
          onOpenChange={(open) => { if (open) load(); }}>
          {button}
        </Popover>
        {raiseDrawer}
      </>
    );
  }

  if (disabled) {
    return <Alert type="info" showIcon message={disabledReason || 'Not needed yet — only required to create a new version once this record is approved'} />;
  }

  return (
    <>
      {listContent}
      {raiseDrawer}
    </>
  );
}
